import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import type {
  FeatureParameterConfig,
  FeatureParameterState,
  FeatureParameterValueOption,
  FeatureParameterValues,
  ParameterValue
} from 'models/FeatureParameter'
import { useSelectedStore } from 'stores/selected'
import { areArraysEqualSets } from 'utils/arrays'
import { useGlobalStore } from 'stores/global'
import { currentLanguage } from 'utils/language'
import { decodeLegacyParameterValue } from 'utils/parameter-url-codec'
import { useModelsStore } from 'stores/models'
import type { DtdlObjectSchema, DtdlSchema } from 'models/Dtdl'

export const ENV_PARAMETER_KEY = '_global'
export const PERMISSIONS_PARAMETER_KEY = '_permissions'
export const ROLES_PARAMETER_KEY = '_roles'
export const LANGUAGE_PARAMETER_KEY = '_language'

export const useParametersStore = defineStore('parameters', () => {
  /** Holds the selected ids */
  const { selectedIds } = storeToRefs(useSelectedStore())
  const { setSelectedByIds } = useSelectedStore()
  const { environment, permissions, environmentRoles: roles } = storeToRefs(useGlobalStore())

  const isStructuredSchema = (schema: DtdlSchema): boolean =>
    typeof schema !== 'string' &&
    (schema['@type'] === 'Array' ||
      schema['@type'] === 'Map' ||
      schema['@type'] === 'Object' ||
      (Array.isArray(schema['@type']) && schema['@type'][0] === 'Object'))

  const isStructuredEnvironmentParameter = (key: string): boolean => {
    const modelId = environment.value?.$metadata?.$model
    if (!modelId) return false
    const parameterValuesProperty = useModelsStore()?.propertyByName(modelId, 'parameterValues')
    const schema = parameterValuesProperty?.schema as DtdlObjectSchema | undefined
    if (schema?.['@type'] !== 'Object' || !schema.fields) return false
    const field = schema.fields.find((candidate) => candidate.name === key)
    return field ? isStructuredSchema(field.schema) : false
  }

  const getEnvironmentParameterValues = (): FeatureParameterValues => {
    // Ensure that the environment is loaded before proceeding
    if (!environment?.value) return {}
    const values = Object.fromEntries(
      Object.entries(environment.value.parameterValues ?? {}).map(
        ([key, value]): [string, ParameterValue] => [
          key,
          isStructuredEnvironmentParameter(key)
            ? decodeLegacyParameterValue(value as ParameterValue)
            : (value as ParameterValue)
        ]
      )
    ) as FeatureParameterValues
    return {
      ...values,
      environmentId: environment.value.$dtId,
      name: environment.value.name
    }
  }
  const environmentParameterValues = computed(getEnvironmentParameterValues)

  const equalParameterValues = (left: ParameterValue | undefined, right: ParameterValue) =>
    JSON.stringify(left) === JSON.stringify(right)

  const stringArrayValue = (value: ParameterValue | undefined): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

  const selectedIdValue = (value: ParameterValue | undefined): string[] => stringArrayValue(value)

  /** Holds the common parameter values for this feature */
  const parameterValues = ref<FeatureParameterValues>({
    [ENV_PARAMETER_KEY]: environmentParameterValues.value,
    [PERMISSIONS_PARAMETER_KEY]: permissions?.value ? Array.from(permissions.value) : [],
    [ROLES_PARAMETER_KEY]: roles?.value ? roles.value.map((r) => r.role) : [],
    [LANGUAGE_PARAMETER_KEY]: currentLanguage.value,
    _selectedIds: selectedIds.value
  })

  /** Update [ENV_PARAMETER_KEY] from useGlobalStore into the parameter values */
  watch(
    environmentParameterValues,
    (val) => {
      if (!equalParameterValues(parameterValues.value[ENV_PARAMETER_KEY], val as ParameterValue)) {
        parameterValues.value = {
          ...parameterValues.value,
          [ENV_PARAMETER_KEY]: val
        }
      }
    },
    // PLEASE USE DEEP OTHERWISE THE WATCHER WILL NOT ALWAYS BE TRIGGERED
    { immediate: true, deep: true }
  )

  watch(
    permissions,
    (val) => {
      const nextValue = val ? Array.from(val) : []
      if (
        !areArraysEqualSets(
          nextValue,
          stringArrayValue(parameterValues.value[PERMISSIONS_PARAMETER_KEY])
        )
      ) {
        parameterValues.value = {
          ...parameterValues.value,
          [PERMISSIONS_PARAMETER_KEY]: nextValue
        }
      }
    },
    // PLEASE USE DEEP OTHERWISE THE WATCHER WILL NOT ALWAYS BE TRIGGERED
    { immediate: true, deep: true }
  )

  watch(
    roles,
    (val) => {
      const nextValue = val ? val.map((r) => r.role) : []
      if (
        !areArraysEqualSets(nextValue, stringArrayValue(parameterValues.value[ROLES_PARAMETER_KEY]))
      ) {
        parameterValues.value = {
          ...parameterValues.value,
          [ROLES_PARAMETER_KEY]: nextValue
        }
      }
    },
    // PLEASE USE DEEP OTHERWISE THE WATCHER WILL NOT ALWAYS BE TRIGGERED
    { immediate: true, deep: true }
  )

  /** Update selectedIds from useSelectedStore into the parameter values */
  watch(
    selectedIds,
    (val) => {
      if (!areArraysEqualSets(val, selectedIdValue(parameterValues.value._selectedIds))) {
        parameterValues.value = {
          ...parameterValues.value,
          _selectedIds: [...val]
        }
      }
    },
    // PLEASE USE DEEP OTHERWISE THE WATCHER WILL NOT ALWAYS BE TRIGGERED
    { deep: true }
  )

  /** Update [LANGUAGE_PARAMETER_KEY] when the current language changes */
  watch(
    currentLanguage,
    (val) => {
      if (parameterValues.value[LANGUAGE_PARAMETER_KEY] !== val) {
        parameterValues.value[LANGUAGE_PARAMETER_KEY] = val
      }
    },
    { immediate: true }
  )

  /** Update selectedIds from parameter-values into useSelectedStore selectedIds */
  watch(
    () => parameterValues.value._selectedIds,
    (val) => {
      const selectedValue = Array.isArray(val) ? val : selectedIdValue(val)
      if (!areArraysEqualSets(selectedIds.value, selectedValue)) {
        setSelectedByIds(selectedValue)
      }
    }
  )

  /** Holds all parameter configs by state key */
  const parameterConfigsMap = ref<{ [stateKey: string]: FeatureParameterConfig }>({})
  /** Convenience getter to get all parameter configs in an array */
  const parameterConfigs = computed(() => Object.values(parameterConfigsMap.value))
  /** Holds parameter keys that are dynamically excluded from the URL (e.g. from scanQRCode actions with disableUrlQueryParameter) */
  const dynamicDisabledUrlQueryParameterKeys = ref<string[]>([])
  /** Holds all parameter options by state key */
  const parameterOptionsMap = ref<{ [stateKey: string]: FeatureParameterValueOption[] }>({})
  /** Tracks which parameter variableNames were intentionally cleared by the user.
   *  Keyed by variableName; true means initialize() and filterParameterValueByOptions()
   *  should not restore the defaultValue or auto-select the first option. */
  const parameterClearedMap = ref<Record<string, boolean>>({})
  /** Holds the parameter state promise (resolves once feature queries are loaded into the global store) */
  const parameterStatePromise = ref<{ [featureId: string]: Promise<unknown> }>({})
  /** Holds all parameter states by state key */
  const parameterStatesMap = ref<{ [stateKey: string]: FeatureParameterState }>({})

  /** Ref used to trigger initialization or repopulate options */
  const refreshRef = ref(false)
  /** Trigger initialization on parameters */
  const initialize = () => {
    refreshRef.value = !refreshRef.value
  }

  /** Ref used to trigger reset (set to default values) */
  const resetRef = ref(false)
  /** Trigger reset on parameters (set to default values) */
  const reset = () => {
    resetRef.value = !resetRef.value
  }

  /** Clear all parameter values and options */
  const clear = () => {
    parameterValues.value = {
      [ENV_PARAMETER_KEY]: environmentParameterValues.value,
      [LANGUAGE_PARAMETER_KEY]: currentLanguage.value,
      _selectedIds: [...selectedIds.value]
    } as FeatureParameterValues
    parameterConfigsMap.value = {}
    parameterOptionsMap.value = {}
    parameterClearedMap.value = {}
    parameterStatesMap.value = {}
    parameterStatePromise.value = {}
    refreshRef.value = false
    resetRef.value = false
  }

  return {
    /** Clear all parameter values and options */
    clear,
    /** Ref used to trigger initialization or repopulate options */
    refreshRef,
    /** Ref used to trigger reset (set to default values) */
    resetRef,
    /** Trigger initialization on parameters */
    initialize,
    refresh: initialize,
    /** Trigger reset on parameters (set to default values) */
    reset,
    parameterConfigsMap,
    parameterConfigs,
    parameterValues,
    parameterOptionsMap,
    parameterClearedMap,
    parameterStatesMap,
    parameterStatePromise,
    dynamicDisabledUrlQueryParameterKeys
  }
})
